/**
 * Decision 0144 (experiment 11, 0136 §4): the lead's brief in fixed sections plus free text. The lead writes the
 * sections and their refs; this renders the markdown every older reader keeps (the bell, `look_back`, a brief from
 * before 0144 reads the same), and ranks "Waiting on you" by what each entry holds up (core `askRank` for cards; the
 * same measure for items). Pure.
 */
import type { BriefSections } from "@minga/protocol"
import { refsIn } from "./knowledge/refs-text"
import { askRank } from "./needs-you"
import { isOpenItem, isRunEnded, itemOf } from "./state"
import { topItemOf } from "./tree"
import type { Item, ProjectState } from "./types"

/** The section headings, in order (people's words; the web uses the same). */
export const BRIEF_SECTION_TITLES = {
  asks: "Questions for you",
  waiting: "Your to-dos",
  didntWork: "Didn't work",
  happened: "What happened",
  next: "Next",
  text: "Also",
} as const

const refList = (refs: ReadonlyArray<string> | undefined): string =>
  refs && refs.length > 0 ? ` (${refs.join(", ")})` : ""

/**
 * The brief's markdown from its sections: the headline in bold, then each non-empty section as a heading with its
 * lines. What a markdown-only reader (the bell before 0144, `look_back`, an older web) shows.
 */
export const renderBriefMarkdown = (b: BriefSections): string => {
  const out: string[] = [`**${b.headline.trim()}**`]
  const section = (title: string, lines: ReadonlyArray<string>) => {
    if (lines.length === 0) return
    out.push("", `### ${title}`, ...lines.map((l) => `- ${l}`))
  }
  section(BRIEF_SECTION_TITLES.asks, b.asks ?? [])
  section(BRIEF_SECTION_TITLES.waiting, b.waiting ?? [])
  section(
    BRIEF_SECTION_TITLES.didntWork,
    (b.didntWork ?? []).map(
      (f) => `${f.text.trim()}${f.needed ? ` Needed: ${f.needed.trim()}` : ""}${refList(f.refs)}`,
    ),
  )
  section(
    BRIEF_SECTION_TITLES.happened,
    (b.happened ?? []).map((l) => `${l.text.trim()}${refList(l.refs)}`),
  )
  section(
    BRIEF_SECTION_TITLES.next,
    (b.next ?? []).map((l) => `${l.text.trim()}${refList(l.refs)}`),
  )
  if (b.text?.trim()) out.push("", b.text.trim())
  return out.join("\n")
}

/** Every ref the sections name, in order, once each (the app looks them up in one call). */
export const briefSectionRefs = (b: BriefSections): ReadonlyArray<string> => {
  const all = [
    ...refsIn(b.headline),
    ...(b.asks ?? []),
    ...(b.waiting ?? []),
    ...(b.didntWork ?? []).flatMap((f) => [...refsIn(f.text), ...(f.refs ?? [])]),
    ...(b.happened ?? []).flatMap((l) => [...refsIn(l.text), ...(l.refs ?? [])]),
    ...(b.next ?? []).flatMap((l) => [...refsIn(l.text), ...(l.refs ?? [])]),
    ...refsIn(b.text ?? ""),
  ]
  return [...new Set(all.map((r) => r.trim()).filter((r) => r !== ""))]
}

/**
 * How much an open item holds up (decision 0134's measure, for items): the live runs of the open work waiting on it by
 * `blocks` (transitively), then how many open items that is, then whether that work sits under a top-level item in Now.
 * The item itself is not counted. Higher first.
 */
export const itemRank = (s: ProjectState, item: Item): number => {
  const held = new Set<string>()
  const queue = s.edges.filter((e) => e.kind === "blocks" && e.from.id === item.id).map((e) => e.to.id)
  while (queue.length > 0) {
    const id = queue.shift() as string
    if (held.has(id) || id === item.id) continue
    const t = itemOf(s, id as never)
    if (!t || !isOpenItem(t)) continue
    held.add(id)
    for (const e of s.edges) if (e.kind === "blocks" && e.from.id === id) queue.push(e.to.id)
  }
  const live = s.runs.filter((r) => r.itemId && held.has(r.itemId) && !isRunEnded(r)).length
  const inNow = [...held].some((id) => {
    const t = itemOf(s, id as never)
    return t !== undefined && topItemOf(s, t).status === "in_progress"
  })
  return live * 1_000_000 + Math.min(held.size, 999) * 1_000 + (inNow ? 1 : 0)
}

/** The open items an item holds up (the "blocks 10" on a brief entry). */
export const itemHolds = (s: ProjectState, item: Item): number =>
  Math.floor((itemRank(s, item) % 1_000_000) / 1_000)

export interface RankedWaiting {
  readonly ref: string
  readonly rank: number
  /** Open items it holds up. */
  readonly holds: number
}

/**
 * "Waiting on you", ranked by what each entry holds up (0133 §5: the person's brief shows unanswered cards ranked by
 * what they block, never a reminder): cards by `askRank`, items by `itemRank`; ties keep the lead's order, then an
 * earlier due date first. Refs the state doesn't know keep their place at the end.
 */
export const rankWaiting = (s: ProjectState, refs: ReadonlyArray<string>): ReadonlyArray<RankedWaiting> => {
  const rows = refs.map((ref, index) => {
    const card = s.proposals.find((p) => p.ref === ref)
    if (card) {
      const rank = askRank(s, card)
      return {
        ref,
        rank,
        holds: Math.floor((rank % 1_000_000) / 1_000),
        index,
        due: Number.POSITIVE_INFINITY,
      }
    }
    const upper = ref.toUpperCase()
    const item = s.items.find((t) => t.ref.toUpperCase() === upper)
    if (item) {
      const rank = itemRank(s, item)
      return { ref, rank, holds: itemHolds(s, item), index, due: item.dueAt ?? Number.POSITIVE_INFINITY }
    }
    return { ref, rank: -1, holds: 0, index, due: Number.POSITIVE_INFINITY }
  })
  rows.sort((a, b) => b.rank - a.rank || a.due - b.due || a.index - b.index)
  return rows.map(({ ref, rank, holds }) => ({ ref, rank, holds }))
}
